import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/brand'
import { LanguageSwitcher } from '@/components/blocks/language-switcher'

const navigation = [
  {
    name: 'Services',
    href: '/services',
    children: [
      { name: 'Strategic Consulting', href: '/services/consulting' },
      { name: 'Custom Development', href: '/services/development' },
      { name: 'Generative AI', href: '/services/platform' },
      { name: 'Managed Services', href: '/services/managed-services' },
    ],
  },
  { name: 'About', href: '/about' },
  { name: 'Case Studies', href: '/case-studies' },
  { name: 'Insights', href: '/insights' },
]

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const location = useLocation()

  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(href + '/')

  return (
    <header className="sticky top-0 z-50 w-full border-b border-neutral-800 bg-neutral-950/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-950/80">
      <nav className="container-lg flex h-20 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <Logo variant="full" theme="dark" size="md" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex md:items-center md:gap-1">
          {navigation.map((item) =>
            item.children ? (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-1 rounded-md px-3 py-2 text-body-sm font-medium transition-colors',
                    isActive(item.href)
                      ? 'text-accent-400'
                      : 'text-neutral-300 hover:text-white'
                  )}
                >
                  {item.name}
                  <ChevronDown className={cn('h-4 w-4 transition-transform', servicesOpen && 'rotate-180')} />
                </Link>
                {servicesOpen && (
                  <div className="absolute left-0 top-full pt-2">
                    <div className="w-56 rounded-lg border border-neutral-700 bg-neutral-900 p-2 shadow-xl">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={cn(
                            'block rounded-md px-3 py-2 text-body-sm transition-colors',
                            isActive(child.href)
                              ? 'bg-accent-400/10 text-accent-400'
                              : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
                          )}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'rounded-md px-3 py-2 text-body-sm font-medium transition-colors',
                  isActive(item.href)
                    ? 'text-accent-400'
                    : 'text-neutral-300 hover:text-white'
                )}
              >
                {item.name}
              </Link>
            )
          )}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex md:items-center md:gap-3">
          <LanguageSwitcher />
          <Link
            to="/contact"
            className="rounded-xl gradient-primary px-4 py-2 text-body-sm font-medium text-white transition-all hover:scale-105 hover:shadow-lg hover:shadow-accent-400/20"
          >
            Contact Us
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md p-2 text-neutral-300 hover:text-white md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span className="sr-only">Toggle menu</span>
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="border-t border-neutral-800 bg-neutral-900 md:hidden">
          <div className="container-lg space-y-1 py-4">
            {navigation.map((item) =>
              item.children ? (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={cn(
                      'block rounded-md px-3 py-2 text-body-md font-medium',
                      isActive(item.href)
                        ? 'bg-accent-400/10 text-accent-400'
                        : 'text-neutral-300'
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  <div className="ml-4 space-y-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.name}
                        to={child.href}
                        className={cn(
                          'block rounded-md px-3 py-2 text-body-sm',
                          isActive(child.href)
                            ? 'text-accent-400'
                            : 'text-neutral-400'
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'block rounded-md px-3 py-2 text-body-md font-medium',
                    isActive(item.href)
                      ? 'bg-accent-400/10 text-accent-400'
                      : 'text-neutral-300'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )
            )}
            <div className="pt-4">
              <Link
                to="/contact"
                className="block rounded-xl gradient-primary px-4 py-2 text-center text-body-md font-medium text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
